import { apiClient, ApiResponse } from './apiClient';

export interface PropertyMedia {
  id: number;
  property_id: number;
  media_type: string;
  url: string;
  alt_text: string;
  title: string;
  description: string;
  display_order: number;
  is_primary: boolean;
  file_size: number;
  file_format: string;
  width?: number;
  height?: number;
  duration?: number;
  is_active: boolean;
  uploaded_by: string;
  createdAt: string;
  updatedAt: string;
}

export interface UploadMediaRequest {
  media: File;
  alt_text: string;
  title: string;
  description: string;
  is_primary: boolean;
}

export class PropertyMediaService {
  private baseUrl = '/properties';

  /**
   * Normalize media URLs to fix common issues:
   * - Remove double slashes
   * - Convert absolute file paths to proper URLs
   * - Ensure URLs are properly formatted
   */
  private normalizeMediaUrl(url: string, propertyId?: number): string {
    if (!url) return url;

    // Get the API base URL from environment or default to localhost:3000
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
    
    // If URL already starts with http/https, it's already a full URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
      // First, fix double slashes after protocol (e.g., http://localhost:3000//path)
      let normalized = url.replace(/(https?:\/\/[^\/]+)\/\/+/g, '$1/');
      
      // Check if the URL contains an absolute file path after the base URL
      // e.g., http://localhost:3000//Users/... or http://localhost:3000/Users/...
      const absolutePathPatterns = [
        /\/Users\//,
        /\/home\//,
        /^[A-Z]:\\/,
        /\/Desktop\//,
        /\/Documents\//
      ];
      
      // Check if URL contains absolute file path
      const hasAbsolutePath = absolutePathPatterns.some(pattern => normalized.match(pattern));
      
      if (hasAbsolutePath) {
        // Try to extract the relative path from the absolute path
        // Look for common upload directories in the path
        const uploadPatterns = [
          /\/uploads\/([^\/]+\.(jpg|jpeg|png|gif|webp|mp4|mov|avi|pdf))$/i,
          /\/properties\/(\d+\/[^\/]+\.(jpg|jpeg|png|gif|webp|mp4|mov|avi|pdf))$/i,
          /([^\/]+\.(jpg|jpeg|png|gif|webp|mp4|mov|avi|pdf))$/i
        ];
        
        for (const pattern of uploadPatterns) {
          const match = normalized.match(pattern);
          if (match) {
            // If we found a properties/ID/filename pattern, use it
            if (normalized.includes('/properties/')) {
              const propertiesMatch = normalized.match(/\/properties\/(\d+\/[^\/]+)$/);
              if (propertiesMatch) {
                return `${apiBaseUrl}/properties/${propertiesMatch[1]}`;
              }
            }
            // If we found an uploads/filename pattern, use it
            if (normalized.includes('/uploads/')) {
              const uploadsMatch = normalized.match(/\/uploads\/([^\/]+)$/);
              if (uploadsMatch) {
                return `${apiBaseUrl}/uploads/${uploadsMatch[1]}`;
              }
            }
            // Otherwise, extract just the filename and try common paths
            const filename = match[1] || match[0];
            if (filename) {
              // Try properties path first (more specific)
              if (normalized.includes('/properties/')) {
                const propertyIdMatch = normalized.match(/\/properties\/(\d+)/);
                if (propertyIdMatch) {
                  return `${apiBaseUrl}/properties/${propertyIdMatch[1]}/${filename}`;
                }
              }
              // If we have propertyId context, use it
              if (propertyId) {
                return `${apiBaseUrl}/properties/${propertyId}/${filename}`;
              }
              // Fallback to uploads
              return `${apiBaseUrl}/uploads/${filename}`;
            }
          }
        }
        
        // Last resort: extract just the filename
        const filename = normalized.split('/').pop() || normalized.split('\\').pop() || '';
        if (filename && filename.match(/\.(jpg|jpeg|png|gif|webp|mp4|mov|avi|pdf)$/i)) {
          // Try to get property ID from URL if available
          const propertyIdMatch = normalized.match(/\/properties\/(\d+)/);
          if (propertyIdMatch) {
            return `${apiBaseUrl}/properties/${propertyIdMatch[1]}/${filename}`;
          }
          // If we have propertyId context, use it
          if (propertyId) {
            return `${apiBaseUrl}/properties/${propertyId}/${filename}`;
          }
          return `${apiBaseUrl}/uploads/${filename}`;
        }
      }
      
      return normalized;
    }
    
    // If URL starts with /, it's a relative path
    if (url.startsWith('/')) {
      // Check if it's an absolute file path
      if (url.match(/^\/Users\//) || url.match(/^\/home\//) || url.match(/^\/Desktop\//)) {
        // Extract filename and construct proper URL
        const filename = url.split('/').pop() || '';
        if (filename && filename.match(/\.(jpg|jpeg|png|gif|webp|mp4|mov|avi|pdf)$/i)) {
          // Try to extract property ID if in path
          const propertyIdMatch = url.match(/\/properties\/(\d+)/);
          if (propertyIdMatch) {
            return `${apiBaseUrl}/properties/${propertyIdMatch[1]}/${filename}`;
          }
          // If we have propertyId context, use it
          if (propertyId) {
            return `${apiBaseUrl}/properties/${propertyId}/${filename}`;
          }
          return `${apiBaseUrl}/uploads/${filename}`;
        }
      }
      // Normal relative path
      return `${apiBaseUrl}${url}`;
    }
    
    // Default: treat as relative path
    return `${apiBaseUrl}/${url}`;
  }

  // Upload media to a property
  async uploadMedia(propertyId: number, data: UploadMediaRequest): Promise<ApiResponse<PropertyMedia>> {
    try {
      console.log('🔧 PropertyMediaService.uploadMedia called:', { propertyId, data });
      
      // Create FormData for file upload matching API format
      const formData = new FormData();
      
      // Validate file exists and is a File object
      if (!data.media) {
        throw new Error('Media file is required');
      }
      
      if (!(data.media instanceof File)) {
        throw new Error('Media must be a File object');
      }
      
      console.log('🔧 File details:', {
        name: data.media.name,
        size: data.media.size,
        type: data.media.type,
        lastModified: data.media.lastModified
      });
      
      // Append the file - API expects 'media' field
      // Include filename explicitly
      formData.append('media', data.media, data.media.name);
      formData.append('alt_text', data.alt_text || '');
      formData.append('title', data.title || '');
      
      // Append is_primary as string
      formData.append('is_primary', String(data.is_primary));
      
      // Always include description field (even if empty) as some APIs require all fields
      formData.append('description', data.description || '');
      
      // Verify FormData contents
      console.log('🔧 FormData entries:');
      const entries = Array.from(formData.entries());
      let fileFound = false;
      entries.forEach(([key, value]) => {
        if (value instanceof File) {
          console.log(`  ✅ ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
          fileFound = true;
        } else {
          console.log(`  - ${key}: ${value}`);
        }
      });
      
      if (!fileFound) {
        throw new Error('File was not added to FormData');
      }
      
      // Use upload method for file uploads (handles FormData correctly)
      const response = await apiClient.upload<PropertyMedia>(
        `${this.baseUrl}/${propertyId}/media`,
        formData
      );
      
      console.log('🔧 PropertyMediaService.uploadMedia response:', response);
      
      // Check if the response indicates failure
      if (response.success === false) {
        const error = new Error(response.message || 'Upload failed');
        (error as any).response = {
          status: response.statusCode || 400,
          data: response
        };
        throw error;
      }
      
      return response;
    } catch (error: any) {
      console.error('🔧 PropertyMediaService.uploadMedia error:', error); 
      
      // Log the full error details
      if (error.response?.data) {
        console.error('🔧 Full error response data:', JSON.stringify(error.response.data, null, 2));
      }
      
      // Extract detailed error message from API response
      let errorMessage = 'Error uploading media';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: undefined,
        message: errorMessage,
        errors: error.response?.data?.errors || []
      };
    }
  }

  // Get media for a property (if needed)
  async getPropertyMedia(propertyId: number): Promise<ApiResponse<PropertyMedia[]>> {
    try {
      console.log('🔧 PropertyMediaService.getPropertyMedia called:', { propertyId });
      
      const response = await apiClient.get<any>(`${this.baseUrl}/${propertyId}/media`);
      
      console.log('🔧 PropertyMediaService.getPropertyMedia raw response:', response);
      console.log('🔧 PropertyMediaService.getPropertyMedia response.data:', response.data);
      console.log('🔧 PropertyMediaService.getPropertyMedia response.data type:', typeof response.data);
      console.log('🔧 PropertyMediaService.getPropertyMedia response.data isArray:', Array.isArray(response.data));
      
      // Handle different response structures
      let mediaArray: PropertyMedia[] = [];
      
      if (response.success && response.data) {
        // Check if data is directly an array
        if (Array.isArray(response.data)) {
          mediaArray = response.data;
        }
        // Check if data has a nested 'media' property
        else if (response.data.media && Array.isArray(response.data.media)) {
          mediaArray = response.data.media;
        }
        // Check if data has a nested 'data' property
        else if (response.data.data && Array.isArray(response.data.data)) {
          mediaArray = response.data.data;
        }
        // Check if it's an object with array-like structure
        else if (typeof response.data === 'object') {
          // Try to find any array property
          const arrayKey = Object.keys(response.data).find(key => Array.isArray(response.data[key]));
          if (arrayKey) {
            mediaArray = response.data[arrayKey];
          }
        }
        
        console.log('🔧 PropertyMediaService.getPropertyMedia extracted mediaArray:', mediaArray);
        console.log('🔧 PropertyMediaService.getPropertyMedia mediaArray length:', mediaArray.length);
        
        // Normalize URLs for all media items
        const normalizedMedia = mediaArray.map((media) => ({
          ...media,
          url: this.normalizeMediaUrl(media.url, propertyId)
        }));
        
        console.log('🔧 PropertyMediaService.getPropertyMedia normalized URLs:', normalizedMedia.map(m => ({ id: m.id, originalUrl: mediaArray.find(orig => orig.id === m.id)?.url, normalizedUrl: m.url })));
        
        return {
          ...response,
          data: normalizedMedia
        };
      }
      
      return {
        ...response,
        data: []
      };
    } catch (error: any) {
      console.error('🔧 PropertyMediaService.getPropertyMedia error:', error);
      console.error('🔧 PropertyMediaService.getPropertyMedia error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      return {
        success: false,
        data: [],
        message: 'Error fetching property media'
      };
    }
  }

  // Delete media from a property (if needed)
  async deleteMedia(propertyId: number, mediaId: number): Promise<ApiResponse<void>> {
    try {
      console.log('🔧 PropertyMediaService.deleteMedia called:', { propertyId, mediaId });
      
      const response = await apiClient.delete<void>(`${this.baseUrl}/${propertyId}/media/${mediaId}`);
      
      console.log('🔧 PropertyMediaService.deleteMedia response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PropertyMediaService.deleteMedia error:', error);
      
      return {
        success: false,
        data: undefined,
        message: 'Error deleting media'
      };
    }
  }

  // Update media (if needed)
  async updateMedia(propertyId: number, mediaId: number, data: Partial<UploadMediaRequest>): Promise<ApiResponse<PropertyMedia>> {
    try {
      console.log('🔧 PropertyMediaService.updateMedia called:', { propertyId, mediaId, data });
      
      const response = await apiClient.patch<PropertyMedia>(`${this.baseUrl}/${propertyId}/media/${mediaId}`, data);
      
      console.log('🔧 PropertyMediaService.updateMedia response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PropertyMediaService.updateMedia error:', error);
      
      return {
        success: false,
        data: undefined,
        message: 'Error updating media'
      };
    }
  }
}

export const propertyMediaService = new PropertyMediaService();
export default propertyMediaService;
